import React, { useState, useEffect, useMemo } from 'react';
import { Container, Spinner, Modal, Form, Row, Col, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import { getAllUsers, addNewUser, getUserHistory, getAllDealers, getIodUser, saveIodUser } from '../../services/user-service';
import { getAllRoles } from '../../services/role-service';
import { refreshUserData } from '../../services/auth-service';
import { getDivisions } from '../../services/division-service';
import { getCompanies, getRegions } from '../../services/iodMaster-service';
import InputBox from '../common/InputBox';
import ResponsiveTable from '../Grid/ResponsiveTable';
import { formatDate } from '../../helpers/dateUtils';

const EMPTY_FORM = {
  userId: '', userName: '', roleCode: '', division: '',
  divisions: [], companies: [], regions: [], dealers: [], status: 'Active',
};



// Which assignment sections to show per role — SYSADMIN gets none
const getRoleAssignments = (roleCode) => {
  if (!roleCode || roleCode === 'SYSADMIN') {
    return { divisions: false, regions: false, companies: false, dealers: false };
  }
  const code = roleCode.toUpperCase();
  return {
    divisions: true,
    regions: true,
    companies: true,
    dealers: code.includes('DEALER') || code.includes('DSE') || code.includes('ASM'),
  };
};

const assignmentBox = { border: '1px solid #F1F1ED', borderRadius: 4, padding: '8px 12px', marginBottom: 12 };

const UserMaster = () => {
  const [users, setUsers]               = useState([]);
  const [roles, setRoles]               = useState([]);   // [{ roleCode, roleName }]
  const [divisions, setDivisions]       = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [allRegions, setAllRegions]     = useState([]);
  const [allDealers, setAllDealers]     = useState([]);
  const [loading, setLoading]           = useState(false);
  const [writeAccess, setWriteAccess]   = useState(false);
  const [reload, setReload]             = useState(0);

  // Add / Edit modal
  const [showModal, setShowModal]       = useState(false);
  const [editingUser, setEditingUser]   = useState(null);
  const [formData, setFormData]         = useState(EMPTY_FORM);
  const [formErrors, setFormErrors]     = useState({});
  const [fieldErrors, setFieldErrors]   = useState({});
  const [duplicateError, setDuplicateError] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // View modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingUser, setViewingUser]     = useState(null);

  // History modal
  const [showHistory, setShowHistory]       = useState(false);
  const [historyData, setHistoryData]       = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage]       = useState(1);
  const HISTORY_PAGE_SIZE = 10;

  const paginatedHistory = useMemo(() => {
    const start = (historyPage - 1) * HISTORY_PAGE_SIZE;
    return historyData.slice(start, start + HISTORY_PAGE_SIZE);
  }, [historyData, historyPage]);
  const historyTotalPages = Math.ceil(historyData.length / HISTORY_PAGE_SIZE);

  const [headers] = useState([
    { displayName: 'Status',      columnName: 'status',      dataType: 'text', switch: true, setStatus, width: 120, displayIndication: true, status: true },
    { displayName: 'User ID',     columnName: 'userId',      dataType: 'text', width: 250, displayIndication: true },
    { displayName: 'User Name',   columnName: 'userName',    dataType: 'text', width: 200, displayIndication: true },
    { displayName: 'Role Code',   columnName: 'roleCode',    dataType: 'text', width: 150, displayIndication: true },
    { displayName: 'Created By',  columnName: 'createdBy',   dataType: 'text', width: 150, displayIndication: true },
    { displayName: 'Created On',  columnName: 'createdDate', dataType: 'date', width: 180, displayIndication: true, formatter: formatDate },
    { displayName: 'Modified By', columnName: 'modifiedBy',  dataType: 'text', width: 150, displayIndication: true },
    { displayName: 'Modified On', columnName: 'modifiedDate',dataType: 'date', width: 180, displayIndication: true, formatter: formatDate },
  ]);

  // ── Load on mount ──────────────────────────────────────────────────────────
  useEffect(() => { // NOSONAR
    loadUsers();
    loadRoles();
    loadDivisions();
    loadCompanies();
    loadRegions();
    loadDealers();
    const raw = sessionStorage.getItem('AuthorizedSessions');
    if (!raw) return;
    try {
      const sessions = JSON.parse(raw);
      for (const parent in sessions) {
        for (const menu of sessions[parent] || []) {
          if (menu.submenu === 'Maintain User' && menu.write === true) {
            setWriteAccess(true);
            return;
          }
        }
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => { if (reload > 0) loadUsers(); }, [reload]);

  // ── Data loaders ───────────────────────────────────────────────────────────
  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await getAllUsers();
      setUsers(res.data || []);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  const loadRoles = async () => {
    try {
      const res = await getAllRoles();
      const currentRole = JSON.parse(sessionStorage.getItem('userData') || '{}').roleCode || '';
      // Treat missing status as Active (migrated roles have no status field)
      const all = (res.data || []).filter(r => !r.status || r.status === 'Active');
      const filtered = currentRole === 'SYSADMIN' ? all : all.filter(r => r.roleCode !== 'SYSADMIN');
      setRoles(filtered);
    } catch (err) {
      toast.error('Failed to load roles');
    }
  };

  const loadDivisions = async () => {
    try {
      const res = await getDivisions();
      setDivisions((res.data || []).map(d => d.divisionCode));
    } catch { /* silent */ }
  };

  const loadCompanies = async () => {
    try {
      const res = await getCompanies();
      setAllCompanies((res.data || []).map(c => c.companyCode || c));
    } catch { /* silent */ }
  };

  const loadRegions = async () => {
    try {
      const res = await getRegions();
      setAllRegions((res.data || []).map(r => r.regionCode || r));
    } catch { /* silent */ }
  };

  const loadDealers = async () => {
    try {
      const res = await getAllDealers();
      setAllDealers((res.data || []).map(d => d.dealerCode));
    } catch { /* silent */ }
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleAdd = () => {
    setEditingUser(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setFieldErrors({});
    setDuplicateError('');
    setShowModal(true);
  };

  const handleEdit = async (user) => {
    setFormErrors({});
    setFieldErrors({});
    setDuplicateError('');
    setLoading(true);
    try {
      const res = await getIodUser(user.userId);
      const full = res?.data || user;
      setEditingUser(full);
      setFormData({
        userId:    full.userId    || full.emailId || '',
        userName:  full.userName  || '',
        roleCode:  full.roleCode  || '',
        division:  full.division  || '',
        divisions: full.divisions || [],
        companies: full.companies || [],
        regions:   full.regions   || [],
        dealers:   full.dealers   || [],
        status:    full.status    || 'Active',
      });
      setShowModal(true);
    } catch {
      toast.error('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setFieldErrors({});
    setDuplicateError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'roleCode') {
        return { ...prev, roleCode: value, divisions: [], companies: [], regions: [], dealers: [] };
      }
      return { ...prev, [name]: value };
    });
    if (name === 'userId') setDuplicateError('');
    setFormErrors(prev => ({ ...prev, [name]: '' }));
    setFieldErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateUserId = async (value) => {
    if (!value || !value.trim()) return 'User ID is required';
    return '';
  };

  const validateUserName = async (value) => {
    if (!value || !value.trim()) return 'User Name is required';
    return '';
  };

  const toggleAssignment = (field, value) => {
    setFormData(prev => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const selectAll = (field, allValues) => {
    setFormData(prev => {
      const isAllSelected = allValues.every(v => (prev[field] || []).includes(v));
      return { ...prev, [field]: isAllSelected ? [] : [...allValues] };
    });
  };

  const validate = async () => {
    const errs = {};
    const userIdErr = await validateUserId(formData.userId);
    if (userIdErr) errs.userId = userIdErr;
    const userNameErr = await validateUserName(formData.userName);
    if (userNameErr) errs.userName = userNameErr;
    if (!formData.roleCode) errs.roleCode = 'Role is required';
    if (!formData.status)   errs.status   = 'Status is required';
    setFormErrors(errs);
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await validate())) return;

    // Duplicate check on Add — compare against already-loaded users list
    if (editingUser === null) {
      const isDuplicate = users.some(
        u => (u.userId || u.emailId || '').toUpperCase() === formData.userId.trim().toUpperCase()
      );
      if (isDuplicate) {
        const msg = `User '${formData.userId.trim()}' already exists. Use Edit to modify.`;
        setDuplicateError(msg);
        toast.error(msg);
        return;
      }
    }

    setLoading(true);
    try {
      const res = editingUser
        ? await saveIodUser({ ...formData, isEdit: true })
        : await addNewUser({ ...formData, isEdit: false, isStatusToggle: false });
      toast.success(res.msg || res.statusMsg || 'Saved successfully');
      const currentUser = JSON.parse(sessionStorage.getItem('userData') || '{}');
      if ((currentUser.userId || '').toUpperCase() === formData.userId.toUpperCase()) await refreshUserData();
      handleCloseModal();
      setReload(p => p + 1);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : err?.msg || 'Failed to save user');
    } finally { setLoading(false); }
  };

  function setStatus(item) { // NOSONAR
    const currentUser = JSON.parse(sessionStorage.getItem('userData') || '{}');
    const updated = { ...item, status: item.status === 'Active' ? 'Inactive' : 'Active', modifiedBy: currentUser.userId || 'system', isStatusToggle: true };
    setLoading(true);
    addNewUser(updated)
      .then(res => {
        if (res.statusCode === '2001' || res.statusCode === '2002' || res.statusCode === '2003') {
          setErrorMessage(res.msg); setShowErrorModal(true);
        } else { toast.success('Status updated successfully'); }
        setReload(p => p + 1);
      })
      .catch(err => { toast.error(typeof err === 'string' ? err : err?.msg || 'Failed to update status'); })
      .finally(() => setLoading(false));
  }

  const viewHistory = async (user) => {
    setShowHistory(true);
    setHistoryLoading(true);
    setHistoryPage(1);
    try {
      const res = await getUserHistory(user.userId);
      const sorted = (res.data || []).sort((a, b) => new Date(b.modifiedDate) - new Date(a.modifiedDate));
      setHistoryData(sorted.map((item, i) => {
        const prev = sorted[i + 1];
        const changes = {};
        if (prev) {
          if (item.userName  !== prev.userName)  changes.userName  = true;
          if (item.roleCode  !== prev.roleCode)  changes.roleCode  = true;
          if (item.status    !== prev.status)    changes.status    = true;
        }
        return { ...item, _changes: changes };
      }));
    } catch { toast.error('Failed to load history'); }
    finally { setHistoryLoading(false); }
  };

  const icons = (item) => {
    const currentRole = JSON.parse(sessionStorage.getItem('userData') || '{}').role || '';
    const isSysAdminItem = item.roleCode === 'SYSADMIN';
    const canEdit = currentRole === 'SYSADMIN' || !isSysAdminItem;
    return [
      { type: 'button', text: 'Edit',    click: (i) => canEdit ? handleEdit(i)  : toast.warn('Only SYSADMIN can edit a SYSADMIN user') },
      { type: 'button', text: 'History', click: (i) => viewHistory(i) },
    ];
  };

  const customCell = (value, header, item) => (
    <span className={item._changes?.[header.columnName] ? 'history-changed' : ''}>{value}</span>
  );

  // ── Role dropdown options ──────────────────────────────────────────────────
  const roleOptions = roles.map(r => ({
    value: r.roleCode,
    label: `${r.roleCode}${r.roleName && r.roleName !== r.roleCode ? ` - ${r.roleName}` : ''}`,
  }));

  const selectedRole = roleOptions.find(o => o.value === formData.roleCode) || null;

  // ── Assignment params for selected role ───────────────────────────────────
  const assignParams = getRoleAssignments(formData.roleCode);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {loading && <div className="spinner-box"><Spinner animation="border" className="spinner" /></div>}

      <Container bsPrefix="main-body">
        <div className="page-title">User Management</div>
        <div className="page-sub-title">Add or Edit User Details</div>
        <ResponsiveTable
          headers={headers} data={users} icons={icons}
          dataType="Users" button="User" handleAdd={handleAdd}
          sessionName="Maintain User" noCheckbox={true} pageSize={10}
          onRowClick={(u) => { setViewingUser(u); setShowViewModal(true); }}
          enableDownload={true}
        />
      </Container>

      {/* ── Add / Edit Modal ── */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{editingUser ? 'Edit User' : 'Add New User'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body style={{ maxHeight: '72vh', overflowY: 'auto' }}>

            {/* Row 1: User ID + User Name */}
            <Row>
              <Col md={6}>
                <InputBox type="text" label="User ID" name="userId" maxLength={100}
                  value={formData.userId} onChange={handleInputChange}
                  readOnly={editingUser !== null} required
                  error={fieldErrors} setError={setFieldErrors}
                  customValidation={validateUserId} />
                {duplicateError && (
                  <div className="text-danger" style={{ fontSize: '0.8rem', marginTop: -8, marginBottom: 8 }}>
                    {duplicateError}
                  </div>
                )}
              </Col>
              <Col md={6}>
                <InputBox type="text" label="User Name" name="userName" maxLength={100}
                  value={formData.userName} onChange={handleInputChange} required
                  error={fieldErrors} setError={setFieldErrors}
                  customValidation={validateUserName} />
              </Col>
            </Row>

            {/* Row 2: Role + Status — plain <select> to avoid react-select prop conflicts */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Role <span style={{ color: '#810055' }}>*</span></Form.Label>
                  <Form.Select
                    name="roleCode"
                    value={formData.roleCode}
                    onChange={handleInputChange}
                    isInvalid={!!formErrors.roleCode}
                    style={{ borderColor: formErrors.roleCode ? '#dc3545' : undefined }}
                  >
                    <option value="">-- Select Role --</option>
                    {roleOptions.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                  {formErrors.roleCode && <div className="text-danger" style={{ fontSize: '0.8rem', marginTop: 2 }}>{formErrors.roleCode}</div>}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status <span style={{ color: '#810055' }}>*</span></Form.Label>
                  <Form.Select name="status" value={formData.status} onChange={handleInputChange}>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Assignment sections — shown only when role requires them */}
            {formData.roleCode && (assignParams.divisions || assignParams.regions || assignParams.companies || assignParams.dealers) && (
              <>
                {assignParams.divisions && (
                  <>
                    <div className="form-label mt-2 mb-1" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      Divisions
                      {divisions.length > 0 && (
                        <Form.Check type="checkbox" id="div-all" label="Select All"
                          style={{ fontSize: '0.8rem', fontWeight: 'normal', marginBottom: 0 }}
                          checked={divisions.every(v => formData.divisions.includes(v))}
                          onChange={() => selectAll('divisions', divisions)} />
                      )}
                    </div>
                    <div style={assignmentBox}>
                      <Row>
                        {divisions.length === 0
                          ? <Col><span style={{ fontSize: '0.85rem', color: '#707070' }}>No divisions available</span></Col>
                          : divisions.map(code => (
                            <Col xs={6} md={4} key={code} className="mb-1">
                              <Form.Check type="checkbox" id={`div-${code}`} label={code}
                                checked={formData.divisions.includes(code)}
                                onChange={() => toggleAssignment('divisions', code)} />
                            </Col>
                          ))}
                      </Row>
                    </div>
                  </>
                )}

                {assignParams.regions && (
                  <>
                    <div className="form-label mb-1" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      Regions
                      {allRegions.length > 0 && (
                        <Form.Check type="checkbox" id="reg-all" label="Select All"
                          style={{ fontSize: '0.8rem', fontWeight: 'normal', marginBottom: 0 }}
                          checked={allRegions.every(v => formData.regions.includes(v))}
                          onChange={() => selectAll('regions', allRegions)} />
                      )}
                    </div>
                    <div style={assignmentBox}>
                      <Row>
                        {allRegions.length === 0
                          ? <Col><span style={{ fontSize: '0.85rem', color: '#707070' }}>No regions available</span></Col>
                          : allRegions.map(code => (
                            <Col xs={6} md={4} key={code} className="mb-1">
                              <Form.Check type="checkbox" id={`reg-${code}`} label={code}
                                checked={formData.regions.includes(code)}
                                onChange={() => toggleAssignment('regions', code)} />
                            </Col>
                          ))}
                      </Row>
                    </div>
                  </>
                )}

                {assignParams.companies && (
                  <>
                    <div className="form-label mb-1" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      Company Codes
                      {allCompanies.length > 0 && (
                        <Form.Check type="checkbox" id="co-all" label="Select All"
                          style={{ fontSize: '0.8rem', fontWeight: 'normal', marginBottom: 0 }}
                          checked={allCompanies.every(v => formData.companies.includes(v))}
                          onChange={() => selectAll('companies', allCompanies)} />
                      )}
                    </div>
                    <div style={assignmentBox}>
                      <Row>
                        {allCompanies.length === 0
                          ? <Col><span style={{ fontSize: '0.85rem', color: '#707070' }}>No companies available</span></Col>
                          : allCompanies.map(code => (
                            <Col xs={4} md={3} key={code} className="mb-1">
                              <Form.Check type="checkbox" id={`co-${code}`} label={code}
                                checked={formData.companies.includes(code)}
                                onChange={() => toggleAssignment('companies', code)} />
                            </Col>
                          ))}
                      </Row>
                    </div>
                  </>
                )}

                {assignParams.dealers && allDealers.length > 0 && (
                  <>
                    <div className="form-label mb-1">Dealers</div>
                    <div style={{ ...assignmentBox, maxHeight: 160, overflowY: 'auto' }}>
                      <Row>
                        {allDealers.map(code => (
                          <Col xs={6} md={4} key={code} className="mb-1">
                            <Form.Check type="checkbox" id={`dlr-${code}`} label={code}
                              checked={formData.dealers.includes(code)}
                              onChange={() => toggleAssignment('dealers', code)} />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </>
                )}

                {/* Selection summary */}
                {(formData.divisions.length > 0 || formData.regions.length > 0 || formData.companies.length > 0 || formData.dealers.length > 0) && (
                  <div style={{ fontSize: '0.8rem', color: '#707070', marginTop: 4 }}>
                    {formData.divisions.length > 0 && <span>Divisions: <strong>{formData.divisions.join(', ')}</strong>&nbsp;&nbsp;</span>}
                    {formData.regions.length   > 0 && <span>Regions: <strong>{formData.regions.join(', ')}</strong>&nbsp;&nbsp;</span>}
                    {formData.companies.length > 0 && <span>Companies: <strong>{formData.companies.join(', ')}</strong>&nbsp;&nbsp;</span>}
                    {formData.dealers.length   > 0 && <span>Dealers: <strong>{formData.dealers.join(', ')}</strong></span>}
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── View Modal ── */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton><Modal.Title>View User</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}><InputBox type="text" label="User ID"   name="userId"   value={viewingUser?.userId   || ''} readOnly /></Col>
            <Col md={6}><InputBox type="text" label="User Name" name="userName" value={viewingUser?.userName || ''} readOnly /></Col>
            <Col md={6}><InputBox type="text" label="Role"      name="roleCode" value={viewingUser?.roleCode || ''} readOnly /></Col>
            <Col md={6}><InputBox type="text" label="Status"    name="status"   value={viewingUser?.status   || ''} readOnly /></Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ── History Modal ── */}
      <Modal show={showHistory} onHide={() => setShowHistory(false)} size="xl" backdrop="static">
        <Modal.Header closeButton><Modal.Title>User History</Modal.Title></Modal.Header>
        <Modal.Body style={{ overflowX: 'hidden', maxHeight: '60vh', overflowY: 'auto', padding: '1rem' }}>
          {historyLoading
            ? <div className="text-center"><Spinner animation="border" /></div>
            : (
              <div className="hide-pagination">
                <ResponsiveTable
                  headers={[
                    { displayName: 'User ID',     columnName: 'userId',      dataType: 'text', width: 250, displayIndication: true },
                    { displayName: 'User Name',   columnName: 'userName',    dataType: 'text', width: 200, displayIndication: true, cssFormatter: customCell },
                    { displayName: 'Role Code',   columnName: 'roleCode',    dataType: 'text', width: 150, displayIndication: true, cssFormatter: customCell },
                    { displayName: 'Status',      columnName: 'status',      dataType: 'text', width: 120, displayIndication: true, status: true, cssFormatter: customCell },
                    { displayName: 'Modified By', columnName: 'modifiedBy',  dataType: 'text', width: 150, displayIndication: true },
                    { displayName: 'Modified On', columnName: 'modifiedDate',dataType: 'date', width: 180, displayIndication: true, formatter: formatDate },
                  ]}
                  data={paginatedHistory} dataType="UserHistory" noCheckbox={true} pageSize={HISTORY_PAGE_SIZE}
                />
              </div>
            )}
          <style>{`.hide-pagination .pagination-container { display: none !important; }`}</style>
        </Modal.Body>
        <div style={{ borderTop: '1px solid #F1F1ED', padding: '1% 2%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#F9F2F6' }}>
          <span style={{ fontSize: 14 }}>
            {(historyPage - 1) * HISTORY_PAGE_SIZE + 1}–{Math.min(historyPage * HISTORY_PAGE_SIZE, historyData.length)} of {historyData.length} items
          </span>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <ChevronLeft  size={18} onClick={() => historyPage > 1 && setHistoryPage(p => p - 1)}
              style={{ cursor: historyPage === 1 ? 'default' : 'pointer', color: historyPage === 1 ? '#b5b9c6' : '#333' }} />
            <span>Page {historyPage} of {historyTotalPages || 1}</span>
            <ChevronRight size={18} onClick={() => historyPage < historyTotalPages && setHistoryPage(p => p + 1)}
              style={{ cursor: historyPage === historyTotalPages ? 'default' : 'pointer', color: historyPage === historyTotalPages ? '#b5b9c6' : '#333' }} />
          </div>
        </div>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistory(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* ── Error Modal ── */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} backdrop="static">
        <Modal.Header closeButton><Modal.Title>Error</Modal.Title></Modal.Header>
        <Modal.Body>{errorMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowErrorModal(false)}>OK</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserMaster;
